/**
 * Football country codes → display name and flag.
 *
 * The backend stores `country` as a **three-letter football code, not ISO
 * 3166-1 alpha-3** (see the backend's CLUBS.md): `ENG`/`SCO`/`WAL` rather than
 * `GBR`, and `GER`/`NED`/`KSA` rather than `DEU`/`NLD`/`SAU`. Mapping a club's
 * country straight through an ISO library therefore produces wrong flags for a
 * good share of the seeded catalog, which is why this table exists.
 *
 * It lives in `shared/` rather than inside the clubs module because the same
 * codes appear on leagues, on player nationality, and on deals — a second copy
 * is how one screen ends up showing a flag another screen cannot.
 *
 * Unknown codes are not an error: the code itself is shown and the flag is
 * omitted. The catalog grows through a seed script, so a code arriving here
 * before this table knows it is expected, and must degrade quietly.
 */

/** Football code → [display name, ISO 3166-1 alpha-2 used to build the flag]. */
const COUNTRIES: Record<string, readonly [name: string, iso2: string]> = {
  ALB: ['Albania', 'AL'],
  ALG: ['Algeria', 'DZ'],
  ANG: ['Angola', 'AO'],
  ARG: ['Argentina', 'AR'],
  ARM: ['Armenia', 'AM'],
  AUS: ['Australia', 'AU'],
  AUT: ['Austria', 'AT'],
  AZE: ['Azerbaijan', 'AZ'],
  BEL: ['Belgium', 'BE'],
  BHR: ['Bahrain', 'BH'],
  BIH: ['Bosnia and Herzegovina', 'BA'],
  BLR: ['Belarus', 'BY'],
  BRA: ['Brazil', 'BR'],
  BFA: ['Burkina Faso', 'BF'],
  BUL: ['Bulgaria', 'BG'],
  CAN: ['Canada', 'CA'],
  CHI: ['Chile', 'CL'],
  CHN: ['China', 'CN'],
  CIV: ['Côte d’Ivoire', 'CI'],
  CMR: ['Cameroon', 'CM'],
  COD: ['DR Congo', 'CD'],
  COL: ['Colombia', 'CO'],
  CPV: ['Cape Verde', 'CV'],
  CRO: ['Croatia', 'HR'],
  CYP: ['Cyprus', 'CY'],
  CZE: ['Czechia', 'CZ'],
  DEN: ['Denmark', 'DK'],
  ECU: ['Ecuador', 'EC'],
  EGY: ['Egypt', 'EG'],
  ENG: ['England', 'GB'],
  ESP: ['Spain', 'ES'],
  EST: ['Estonia', 'EE'],
  FIN: ['Finland', 'FI'],
  FRA: ['France', 'FR'],
  GAB: ['Gabon', 'GA'],
  GEO: ['Georgia', 'GE'],
  GER: ['Germany', 'DE'],
  GHA: ['Ghana', 'GH'],
  GRE: ['Greece', 'GR'],
  GUI: ['Guinea', 'GN'],
  HUN: ['Hungary', 'HU'],
  IDN: ['Indonesia', 'ID'],
  IND: ['India', 'IN'],
  IRL: ['Republic of Ireland', 'IE'],
  IRN: ['Iran', 'IR'],
  IRQ: ['Iraq', 'IQ'],
  ISL: ['Iceland', 'IS'],
  ISR: ['Israel', 'IL'],
  ITA: ['Italy', 'IT'],
  JOR: ['Jordan', 'JO'],
  JPN: ['Japan', 'JP'],
  KAZ: ['Kazakhstan', 'KZ'],
  KOR: ['South Korea', 'KR'],
  KSA: ['Saudi Arabia', 'SA'],
  KUW: ['Kuwait', 'KW'],
  LBN: ['Lebanon', 'LB'],
  LBY: ['Libya', 'LY'],
  LTU: ['Lithuania', 'LT'],
  LUX: ['Luxembourg', 'LU'],
  LVA: ['Latvia', 'LV'],
  MAR: ['Morocco', 'MA'],
  MDA: ['Moldova', 'MD'],
  MEX: ['Mexico', 'MX'],
  MKD: ['North Macedonia', 'MK'],
  MLI: ['Mali', 'ML'],
  MLT: ['Malta', 'MT'],
  MNE: ['Montenegro', 'ME'],
  NED: ['Netherlands', 'NL'],
  NGA: ['Nigeria', 'NG'],
  NIR: ['Northern Ireland', 'GB'],
  NOR: ['Norway', 'NO'],
  NZL: ['New Zealand', 'NZ'],
  OMA: ['Oman', 'OM'],
  PAR: ['Paraguay', 'PY'],
  PER: ['Peru', 'PE'],
  POL: ['Poland', 'PL'],
  POR: ['Portugal', 'PT'],
  QAT: ['Qatar', 'QA'],
  ROU: ['Romania', 'RO'],
  RSA: ['South Africa', 'ZA'],
  RUS: ['Russia', 'RU'],
  SCO: ['Scotland', 'GB'],
  SEN: ['Senegal', 'SN'],
  SRB: ['Serbia', 'RS'],
  SUI: ['Switzerland', 'CH'],
  SVK: ['Slovakia', 'SK'],
  SVN: ['Slovenia', 'SI'],
  SWE: ['Sweden', 'SE'],
  SYR: ['Syria', 'SY'],
  THA: ['Thailand', 'TH'],
  TUN: ['Tunisia', 'TN'],
  TUR: ['Türkiye', 'TR'],
  UAE: ['United Arab Emirates', 'AE'],
  UKR: ['Ukraine', 'UA'],
  URU: ['Uruguay', 'UY'],
  USA: ['United States', 'US'],
  UZB: ['Uzbekistan', 'UZ'],
  VEN: ['Venezuela', 'VE'],
  WAL: ['Wales', 'GB'],
  ZAM: ['Zambia', 'ZM'],
};

/**
 * The home nations have no ISO alpha-2 of their own, so their flags are emoji
 * tag sequences rather than a pair of regional indicators. Mapping them to `GB`
 * above keeps the name lookup honest; these override the flag.
 */
const SUBDIVISION_FLAGS: Record<string, string> = {
  ENG: '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
  SCO: '🏴󠁧󠁢󠁳󠁣󠁴󠁿',
  WAL: '🏴󠁧󠁢󠁷󠁬󠁳󠁿',
};

/**
 * Every football code we know, ordered by display name.
 *
 * For pickers that need the whole world — a player's nationality can be any
 * country, unlike a club's, which comes from the seeded catalog. Sorted by name
 * rather than by code because that is the order the labels appear in.
 */
export function countryCodes(): string[] {
  return Object.keys(COUNTRIES).sort((a, b) =>
    (COUNTRIES[a]?.[0] ?? a).localeCompare(COUNTRIES[b]?.[0] ?? b),
  );
}

/**
 * What the squad importer writes when a data provider's nationality could not
 * be matched to a football code.
 *
 * Deliberately not a real code, so it is obvious on sight and trivial to grep
 * for. It is left in the data rather than nulled because the column is required
 * and because the row is exactly what a reviewer is being asked to fix — see
 * the backend's `squad-import.service.ts`. Render it through a label rather
 * than raw, or it reads as a rendering bug.
 */
export const UNKNOWN_COUNTRY = 'XXX';

/** Human-readable country name, falling back to the code itself. */
export function countryName(code: string | null | undefined): string {
  if (!code) return '—';
  return COUNTRIES[code.toUpperCase()]?.[0] ?? code;
}

/** Flag emoji for a football country code, or an empty string when unknown. */
export function countryFlag(code: string | null | undefined): string {
  if (!code) return '';

  const upper = code.toUpperCase();
  const subdivision = SUBDIVISION_FLAGS[upper];
  if (subdivision) return subdivision;

  const iso2 = COUNTRIES[upper]?.[1];
  if (!iso2) return '';

  // A flag emoji is the country's two letters as regional indicator symbols.
  return String.fromCodePoint(
    ...[...iso2].map((letter) => 0x1f1e6 + letter.charCodeAt(0) - 'A'.charCodeAt(0)),
  );
}
